import { DistributionPriority } from '@nimbus-global/features/shared/models/domain/distribution-priority.enum';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { CallerType } from '@nimbus-library/features/shared/models/enum/caller-type.enum';
import { CombinedConversationState } from '@nimbus-library/features/shared/models/enum/conversation-status.enum';
import { DispatcherType } from '@nimbus-library/features/shared/models/enum/dispatcher-type.enum';
import { CustomerInformation } from '@nimbus-library/features/shared/models/participant-info/caller-participant-information.model';
import { Icon, ICONS } from '@nimbus-library/features/shared/resources/icons';
import { ConversationTaskType } from '@nimbus-global/features/reportingLiveData/models/task-type.enum';
import { PortalTranslationKeys } from '@nimbus-library/features/localization/models/enums/portal-translation-keys.enum';
import { ChatServiceType } from '@nimbus-global/features/chat/models/chat.model';

export enum ConversationDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
  None = 'None',
}

export enum ConversationOutcome {
  Unknown = 'Unknown',
  Connected = 'Connected',
  Incoming = 'Incoming',
  Ongoing = 'Ongoing',
  Transferring = 'Transferring',
  Accepted = 'Accepted',
  Declined = 'Declined',
  Rona = 'Rona',
  Abandoned = 'Abandoned',
  InAfterCallWork = 'InAfterCallWork',
  InExtendedAfterCallWork = 'InExtendedAfterCallWork',
  Parked = 'Parked',
  PutOnHold = 'PutOnHold',
  DialingOut = 'DialingOut',
  DestinationAccepted = 'DestinationAccepted',
  DestinationDeclined = 'DestinationDeclined',
  DestinationNotReached = 'DestinationNotReached',
  OutboundUserNotAccepted = 'OutboundUserNotAccepted',
  OutboundUserAborted = 'OutboundUserAborted',
  Canceled = 'Canceled',
  Failed = 'Failed',
}

export enum InitialModality {
  Audio = 'Audio',
  InstantMessaging = 'InstantMessaging',
  ExternalTask = 'ExternalTask',
  Email = 'Email',
}

export enum ConversationOriginType {
  Caller = 'Caller',
  Agent = 'Agent',
  TransferToService = 'TransferToService',
  TransferOnUserExit = 'TransferOnUserExit',
  ConsultationTransferToService = 'ConsultationTransferToService',
  ExternalTask = 'ExternalTask',
  SystemGenerated = 'SystemGenerated',
}

export enum ChatChannelType {
  TwilioWhatsapp = 'TwilioWhatsapp',
  TwilioSms = 'TwilioSms',
  TwilioRcs = 'TwilioRcs',
  AcsCalling = 'AcsCalling',
}
/**
 * Nimbus Unify calls run over ACS and are surfaced with the `AcsCalling` channel.
 * Call controls (park/unpark/hold/resume/hangup) are Nimbus Extend capabilities and are
 * not available for Unify, so they must stay hidden on such conversations.
 */
export function isUnifyCall(channel?: ChatChannelType): boolean {
  return channel === ChatChannelType.AcsCalling;
}

export const ChatServiceTypeMapper: Record<ChatChannelType, ChatServiceType> = {
  [ChatChannelType.TwilioWhatsapp]: ChatServiceType.Whatsapp,
  [ChatChannelType.TwilioSms]: ChatServiceType.Sms,
  [ChatChannelType.TwilioRcs]: ChatServiceType.Rcs,
  [ChatChannelType.AcsCalling]: null,
};

export const conversationDirectionTranslationMapper: Record<ConversationDirection, TranslationKeys> = {
  [ConversationDirection.Inbound]: TranslationKeys.GeneralConversationDirectionInbound,
  [ConversationDirection.Outbound]: TranslationKeys.GeneralConversationDirectionOutbound,
  [ConversationDirection.None]: TranslationKeys.GeneralConversationDirectionNone,
};

export const conversationDirectionAudioVideoTranslationMapper: Record<ConversationDirection, TranslationKeys> = {
  [ConversationDirection.Inbound]: TranslationKeys.GeneralConversationDirectionInboundCall,
  [ConversationDirection.Outbound]: TranslationKeys.GeneralConversationDirectionOutboundCall,
  [ConversationDirection.None]: TranslationKeys.GeneralConversationDirectionNone,
};

export function getDirectionModalityIcon(initialModality: InitialModality, isOutbound: boolean, taskType: ConversationTaskType): Icon {
  if (initialModality === InitialModality.InstantMessaging) {
    return ICONS.COMMENT_ALT;
  }
  if (initialModality === InitialModality.ExternalTask) {
    return ICONS.EXTERNAL_TASK;
  }
  if (initialModality === InitialModality.Email) {
    return ICONS.ENVELOPE;
  }
  if (taskType === ConversationTaskType.OutboundCallOnBehalf) {
    return ICONS.DIAL_PAD;
  }
  if (taskType === ConversationTaskType.ConsultationCallWithWorkflow) {
    return ICONS.CONSULTATION_CALL;
  }
  return isOutbound ? ICONS.PHONE_OUTBOUND : ICONS.PHONE_INBOUND;
}

export function getDirectionModalityTooltipKey(
  initialModality: InitialModality,
  isOutbound: boolean,
  taskType: ConversationTaskType,
): TranslationKeys {
  if (initialModality === InitialModality.InstantMessaging) {
    return TranslationKeys.GeneralInstantMessaging;
  }
  if (initialModality === InitialModality.ExternalTask) {
    return TranslationKeys.GeneralExternalTask;
  }
  if (initialModality === InitialModality.Email) {
    return TranslationKeys.GeneralEmail;
  }
  if (taskType === ConversationTaskType.OutboundCallOnBehalf) {
    return TranslationKeys.GeneralCallOnBehalf;
  }
  if (taskType === ConversationTaskType.ConsultationCallWithWorkflow) {
    return TranslationKeys.GeneralConsultationCall;
  }
  return conversationDirectionAudioVideoTranslationMapper[isOutbound ? ConversationDirection.Outbound : ConversationDirection.Inbound];
}

export const conversationOutcomeTranslationMapper: Partial<Record<ConversationOutcome, TranslationKeys>> = {
  [ConversationOutcome.Accepted]: TranslationKeys.GeneralUserInteractionOutcomeAccepted,
  [ConversationOutcome.Connected]: TranslationKeys.GeneralUserInteractionOutcomeConnected,
  [ConversationOutcome.Ongoing]: TranslationKeys.GeneralUserInteractionOutcomeConnected,
  [ConversationOutcome.Parked]: TranslationKeys.GeneralUserInteractionOutcomeConnected,
  [ConversationOutcome.PutOnHold]: TranslationKeys.GeneralUserInteractionOutcomeConnected,
  [ConversationOutcome.Transferring]: TranslationKeys.GeneralUserInteractionOutcomeConnected,
  [ConversationOutcome.Abandoned]: TranslationKeys.GeneralUserInteractionOutcomeCancelled,
  [ConversationOutcome.Declined]: TranslationKeys.GeneralUserInteractionOutcomeDeclined,
  [ConversationOutcome.Incoming]: TranslationKeys.GeneralUserInteractionOutcomeIncoming,
  [ConversationOutcome.Rona]: TranslationKeys.GeneralUserInteractionOutcomeRona,
  [ConversationOutcome.Unknown]: TranslationKeys.GeneralUserInteractionOutcomeUnknown,
  [ConversationOutcome.InAfterCallWork]: TranslationKeys.GeneralUserInteractionOutcomeInAfterCallWork,
  [ConversationOutcome.InExtendedAfterCallWork]: TranslationKeys.GeneralUserInteractionOutcomeInExtendedAfterCallWork,
  [ConversationOutcome.DialingOut]: TranslationKeys.GeneralUserInteractionOutcomeAccepted,
  [ConversationOutcome.OutboundUserNotAccepted]: TranslationKeys.GeneralUserInteractionOutcomeNotAccepted,
  [ConversationOutcome.OutboundUserAborted]: TranslationKeys.GeneralUserInteractionOutcomeUserAborted,
  [ConversationOutcome.DestinationDeclined]: TranslationKeys.GeneralUserInteractionOutcomeDestinationDeclined,
  [ConversationOutcome.DestinationNotReached]: TranslationKeys.GeneralUserInteractionOutcomeNotReached,
  [ConversationOutcome.DestinationAccepted]: TranslationKeys.GeneralUserInteractionOutcomeDestinationAccepted,
};

export const initialModalityTranslationMapper: Record<InitialModality, TranslationKeys> = {
  [InitialModality.Audio]: TranslationKeys.GeneralInitialModalityAudio,
  [InitialModality.InstantMessaging]: TranslationKeys.GeneralInitialModalityInstantMessaging,
  [InitialModality.ExternalTask]: TranslationKeys.GeneralInitialModalityExternalTask,
  [InitialModality.Email]: TranslationKeys.GeneralInitialModalityEmail,
};

export const initialModalityFullTranslationMapper: Record<InitialModality, TranslationKeys> = {
  [InitialModality.Audio]: TranslationKeys.HistoricalSessionsFilterInitialModalityAv,
  [InitialModality.InstantMessaging]: TranslationKeys.HistoricalSessionsFilterInitialModalityIm,
  [InitialModality.ExternalTask]: TranslationKeys.HistoricalSessionsFilterInitialModalityEt,
  [InitialModality.Email]: TranslationKeys.HistoricalSessionsFilterInitialModalityEm,
};

export interface CallerInformation {
  id: string;
  customer: CustomerInformation;
  callerId: string;
  callerTelNumber: string;
  callerUpn: string;
  destinationId: string;
  destinationTelNumber: string;
  destinationUpn: string;
  tenantId: string;
  customContextParameters: Record<string, string>;
  calculatedDisplayName: string;
}

export interface AttendantConsoleConversation {
  conversationId: string;
  teamId: string;
  callerId: ConversationCaller;
  serviceSessionId: string;
  since: string;
  callerInformation: CallerInformation;
  serviceContactInfo: IServiceInformation;
  unifiedConversationId?: string;
  dispatcherType?: DispatcherType;
  distributionPriority?: DistributionPriority;
  callDirection?: ConversationDirection;
  initialModality: InitialModality;
  originType?: ConversationOriginType;
  channel?: ChatChannelType;
  startedAt: string;
  acceptedAt: string;
  taskType: ConversationTaskType;
  startedWithTransfer?: boolean;
  conversationContextLinks?: string[];
  pickupDisableReason?: string;
}

export interface AttendantConsoleOngoingConversation extends AttendantConsoleConversation {
  userSessionId: string;
  distributionAlgorithm?: any;
  currentState: CombinedConversationState;
  consultationSession: ConsultationSession;
  isSupervisionSessionInProgress: boolean;
  outcome?: ConversationOutcome;
  enteredQueueAt?: string;
  unparkRequestedAt: string;
  callerHungUpAt?: string;
  transferStartedAt?: string;
  transferCompletedAt?: string;
  conversationTerminatedAt: string;
  timeInQueue?: string;
  afterCallWorkAllowToStop: boolean;
}

export interface IAttendantConsoleConversations {
  conversationsInQueue: AttendantConsoleConversation[];
  conversationsInProgress: AttendantConsoleOngoingConversation[];
  conversationsInACW: AttendantConsoleOngoingConversation[];
  conversationsTerminated: AttendantConsoleOngoingConversation[];
}

export enum ConsultationSessionState {
  Starting = 'Starting',
  Ongoing = 'Ongoing',
  PutOnHold = 'PutOnHold',
  Completed = 'Completed',
  Conferenced = 'Conferenced',
  Canceled = 'Canceled',
}

// noinspection JSUnusedGlobalSymbols
export enum ConsultationSessionTerminationReason {
  Unknown = 'Unknown',
  Declined = 'Declined',
  Ignored = 'Ignored',
  Conferenced = 'Conferenced',
  Transferred = 'Transferred',
  Terminated = 'Terminated',
  Busy = 'Busy',
  Canceled = 'Canceled',
  Failed = 'Failed',
}

export enum SupervisionMode {
  None = 'None',
  BargeIn = 'BargeIn',
  Whisper = 'Whisper',
  Listen = 'Listen',
}

export interface ISupervisionSession {
  id: string;
  supervisorUserId: string;
  mode: SupervisionMode;
  state: any;
  updatedAt: string;
  canBargeIn: boolean;
  canWhisper: boolean;
  canListen: boolean;
}

export interface ConsultationSessionTarget {
  id: string;
  type: CallerType;
  displayName: string;
}

export interface ConsultationSessionServiceTarget {
  tenantId: string;
  teamId: string;
  description: string;
  displayName: string;
  name: string;
  telNumber: string;
  upn: string;
}

export enum ConsultationConversationState {
  Establishing = 'Establishing',
  InIvr = 'InIvr',
  InQueue = 'InQueue',
  Ringing = 'Ringing',
  DialingOut = 'DialingOut',
  Ongoing = 'Ongoing',
  Transferring = 'Transferring',
  PutOnHold = 'PutOnHold',
  Parked = 'Parked',
  InIvrAfterQueue = 'InIvrAfterQueue',
  Terminated = 'Terminated',
}

export const consultationConversationStateClassNames: Record<ConsultationConversationState, string> = {
  [ConsultationConversationState.InIvr]: 'in-ivr',
  [ConsultationConversationState.InQueue]: 'in-queue',
  [ConsultationConversationState.InIvrAfterQueue]: 'in-ivr-after-queue',
  [ConsultationConversationState.Parked]: 'parked',
  [ConsultationConversationState.Ongoing]: 'ongoing',
  [ConsultationConversationState.Terminated]: 'completed',
  [ConsultationConversationState.Establishing]: 'consultation-default-state',
  [ConsultationConversationState.Ringing]: 'consultation-default-state',
  [ConsultationConversationState.DialingOut]: 'consultation-default-state',
  [ConsultationConversationState.Transferring]: 'consultation-default-state',
  [ConsultationConversationState.PutOnHold]: 'consultation-default-state',
};

export const consultationConversationStateTranslations: Record<ConsultationConversationState, TranslationKeys> = {
  [ConsultationConversationState.Establishing]: PortalTranslationKeys.ConversationStatesEstablishing,
  [ConsultationConversationState.InIvr]: PortalTranslationKeys.ConversationStatesInIvr,
  [ConsultationConversationState.InQueue]: PortalTranslationKeys.ConversationStatesInQueue,
  [ConsultationConversationState.Ringing]: PortalTranslationKeys.ConversationStatesRinging,
  [ConsultationConversationState.DialingOut]: PortalTranslationKeys.ConversationStatesDialingOut,
  [ConsultationConversationState.Ongoing]: PortalTranslationKeys.ConversationStatesOngoing,
  [ConsultationConversationState.Transferring]: PortalTranslationKeys.ConversationStatesTransferring,
  [ConsultationConversationState.PutOnHold]: PortalTranslationKeys.ConversationStatesPutOnHold,
  [ConsultationConversationState.Parked]: PortalTranslationKeys.ConversationStatesParked,
  [ConsultationConversationState.InIvrAfterQueue]: PortalTranslationKeys.ConversationStatesInIvrAfterQueue,
  [ConsultationConversationState.Terminated]: PortalTranslationKeys.ConversationStatesTerminated,
};

export interface ConsultationSessionTargetState {
  state: ConsultationConversationState;
  since: string;
}

export interface ConversationCaller {
  id: string;
  type: CallerType;
}

/**
 * Wire shape returned by the Operations customer-search autocomplete:
 * the underlying caller plus the best display name available (Graph for
 * Guid callers, raw id otherwise). Lets the dropdown show a recognizable
 * label while still filtering by the raw id server-side.
 */
export interface CallerSearchResult {
  callerId: ConversationCaller;
  displayName: string;
}

/**
 * Flattened caller-search result used by store state / view layer:
 * keeps the existing `id`/`type` shape so component code can keep using
 * `customer.id` for routing and filtering, and adds the resolved Graph
 * display name as an optional label for the dropdown.
 */
export interface ConversationCallerWithDisplayName extends ConversationCaller {
  displayName?: string;
}

export enum ConsultationType {
  User = 'User',
  Service = 'Service',
}

export enum ConsultationDirection {
  Source = 'Source',
  Target = 'Target',
}

export interface ConsultationSession {
  id: string;
  currentState: ConsultationSessionState;
  since: string;
  consultationType: ConsultationType;
  direction: ConsultationDirection;
  terminationReason: ConsultationSessionTerminationReason;
  targetService: ConsultationSessionServiceTarget;
  targetAgent: ConsultationSessionTarget;
  targetState: ConsultationSessionTargetState;
  isTargetTerminated: boolean;
  sourceService: ConsultationSessionServiceTarget;
  sourceAgent: ConsultationSessionTarget;
  isSourceTerminated: boolean;
}

export interface ICallerInformation {
  origin: string;
  type: CallerType;
  callId: string;
}

export interface IServiceInformation {
  id: string;
  teamId: string;
  name: string;
  description: string;
  serviceName: string;
  pendingServiceName: string;
  serviceUpn: string;
  pstnNumber: string;
}
